import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/page-header';
import { HelpButton } from '../components/ui/help-button';
import { FeedbackButton } from '../components/ui/feedback-button';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { KpiStrip } from '../components/ui/kpi-strip';
import { RightPanel } from '../components/ui/right-panel';
import { ChainVisual } from '../components/ui/chain-visual';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  AlertTriangle,
  Shield,
  Users,
  Settings,
  Cpu,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Target,
  Info,
  X,
  ChevronRight,
  ClipboardList,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Clock,
  Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { ContextualTip } from '../components/ContextualTip';
import DecisionRow from '../components/DecisionRow';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

import { SectionTabs, SECTIONS, PageShell, PageToolbar } from '../components/enterprise';

const FinancialPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [exposureData, setExposureData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [riskFinancials, setRiskFinancials] = useState([]);
  const [postureCounts, setPostureCounts] = useState({ vermijdend: 0, neutraal: 0, dragend: 0 });
  const [actions, setActions] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [decisionAction, setDecisionAction] = useState(null);
  const [decisionStep, setDecisionStep] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, finDataRes, exposureRes, actionsRes] = await Promise.all([
          api.get('/ggrc/financial/overview'),
          api.get('/ggrc/financial/data'),
          api.get('/risks/exposure/summary').catch(() => ({ data: null })),
          api.get('/actions').catch(() => ({ data: [] }))
        ]);
        
        const overview = overviewRes.data;
        setRiskFinancials(overview.risks || []);
        setPostureCounts(overview.summary?.posture_counts || { vermijdend: 0, neutraal: 0, dragend: 0 });
        setFinancialData(finDataRes.data);
        
        // Use overview data to populate exposure if available, else fallback
        if (exposureRes.data) {
          setExposureData(exposureRes.data);
        } else {
          setExposureData({
            total_annual_exposure: overview.summary?.total_cost_of_inaction || 0,
            mitigated_exposure: overview.summary?.total_cost_of_measures || 0,
            unmitigated_exposure: (overview.summary?.total_cost_of_inaction || 0) - (overview.summary?.total_cost_of_measures || 0),
          });
        }
        
        setActions(actionsRes.data || []);
      } catch (error) {
        toast.error('De financiële gegevens konden niet worden geladen.');
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyOrMissing = (amount, fallback = 'Nog niet berekenbaar') => {
    if (amount === null || amount === undefined || amount === 0) return fallback;
    return formatCurrency(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(0)}K`;
    }
    return formatCurrency(amount);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'people':
        return <Users className="h-5 w-5" strokeWidth={1.5} />;
      case 'process':
        return <Settings className="h-5 w-5" strokeWidth={1.5} />;
      case 'technology':
        return <Cpu className="h-5 w-5" strokeWidth={1.5} />;
      default:
        return <Shield className="h-5 w-5" strokeWidth={1.5} />;
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'people': return 'Mensen';
      case 'process': return 'Processen';
      case 'technology': return 'Technologie';
      default: return category;
    }
  };

  if (loading) {
    return <PageShell section="verbeteren" testId="financial-page" loading />;
  }

  const totalExposure = exposureData?.total_annual_exposure || 0;
  const mitigated = exposureData?.mitigated_exposure || 0;
  const unmitigated = exposureData?.unmitigated_exposure || 0;
  const mitigatedPercentage = totalExposure > 0 ? Math.round((mitigated / totalExposure) * 100) : 0;

  // Calculate top risk actions by cost
  const topRiskActions = actions
    .filter(a => !a.completed_at && a.cost_of_inaction > 0)
    .sort((a, b) => b.cost_of_inaction - a.cost_of_inaction)
    .slice(0, 5);

  return (
    <PageShell section="verbeteren" testId="financial-page">
      <PageToolbar
        title="Financieel"
        subtitle={riskFinancials.length === 0 ? 'Voeg risico-impact toe voor financieel inzicht'
          : postureCounts.dragend > 0 ? `${postureCounts.dragend} risico's missen nog een oplossing`
          : 'Je financiële risico\'s zijn onder controle'}
        testId="financial"
      />

      <div className="px-6 py-6">
          <ContextualTip pageKey="financial" tip="De kolom 'Schade bij niets doen' helpt je bepalen welke risico's het meest urgent zijn om aan te pakken." />

          <main className="space-y-10">
            {/* STATUS */}
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="financial-status-section">
              <div className="flex items-center gap-2">
                <p className="spk-section-label mb-0">Status</p>
                {postureCounts.dragend > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Actie vereist</span>
                )}
              </div>
              {postureCounts.dragend > 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">
                    {postureCounts.dragend} {postureCounts.dragend === 1 ? 'risico heeft' : 'risico\'s hebben'} geen enkele oplossing
                  </h2>
                  <p className="mt-3 text-neutral-600">
                    Dit betekent dat je bewust of onbewust de mogelijke schade accepteert.{totalExposure > 0 ? ` De geschatte impact is ${formatCurrencyShort(totalExposure)}.` : ' Vul impactschattingen in om de geschatte schade te berekenen.'}
                  </p>
                  <Button
                    onClick={() => navigate('/risks')}
                    className="mt-4 bg-black text-white hover:bg-neutral-800"
                    data-testid="financial-to-risks"
                  >
                    Bekijk welke risico's ongedekt zijn
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </>
              ) : riskFinancials.length > 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">
                    Alle risico's worden aangepakt
                  </h2>
                  <p className="mt-3 text-neutral-600">
                    Je hebt voor elk risico minimaal een oplossing. Controleer of de investering opweegt tegen de potentiele schade.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mt-2">Er zijn nog geen risico's in kaart gebracht</h2>
                  <p className="mt-3 text-neutral-600">
                    Start de nulmeting om risico's te identificeren en hun financiele impact te berekenen.
                  </p>
                  <Button
                    onClick={() => navigate('/assessment')}
                    className="mt-4 bg-black text-white hover:bg-neutral-800"
                    data-testid="financial-to-assessment"
                  >
                    Start nulmeting
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </>
              )}
            </section>

            {/* Guided Autonomy: Financiële aanbevelingen */}
            {riskFinancials.length > 0 && (
              <div className="space-y-3" data-testid="financial-decisions">
                <DecisionRow
                  label="Jaarlijkse schade-inschatting"
                  value={totalExposure > 0 ? formatCurrency(totalExposure) : 'Nog niet berekend'}
                  type={totalExposure > 0 ? 'auto' : 'required'}
                  rationale={totalExposure > 0
                    ? `Gebaseerd op ${riskFinancials.length} risico's. Hiervan is ${formatCurrencyShort(mitigated)} (${mitigatedPercentage}%) afgedekt door maatregelen.`
                    : 'Voeg impactschattingen toe aan je risico\'s om de totale schade te berekenen.'}
                  confirmed={totalExposure > 0}
                  onEdit={totalExposure > 0 ? () => navigate('/risks') : undefined}
                  onConfirm={totalExposure === 0 ? () => navigate('/risks') : undefined}
                  editLabel="Pas aannames aan"
                  confirmLabel="Risico's invullen"
                />

                {unmitigated > 0 && (
                  <DecisionRow
                    label="Ongedekte schade"
                    value={formatCurrency(unmitigated)}
                    type="recommended"
                    rationale={`${postureCounts.dragend} risico's hebben nog geen maatregel. Koppel oplossingen om deze schade te verlagen.`}
                    onConfirm={() => navigate('/risks')}
                    confirmLabel="Bekijk ongedekte risico's"
                  />
                )}

                {topRiskActions.length > 0 && (
                  <DecisionRow
                    label="Beste investering"
                    value={topRiskActions[0]?.name || 'Onbekend'}
                    type="recommended"
                    rationale={`Voorkomt naar schatting ${formatCurrencyShort(topRiskActions[0]?.cost_of_inaction || 0)} aan schade. ${topRiskActions.length > 1 ? `Nog ${topRiskActions.length - 1} andere acties beschikbaar.` : ''}`}
                    onConfirm={() => navigate('/measures')}
                    confirmLabel="Bekijk maatregelen"
                  />
                )}
              </div>
            )}

        {/* Risicohouding per risico */}
        {riskFinancials.length > 0 && (
        <section className="rounded-2xl border border-neutral-200 p-7" data-testid="risk-posture-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="spk-section-label">Per risico</p>
              <p className="text-xs text-neutral-400 mt-0.5">Wat kost het als je niets doet, en wat kost de oplossing?</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                <span className="text-[10px] text-neutral-500">Aangepakt ({postureCounts.vermijdend})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                <span className="text-[10px] text-neutral-500">Deels ({postureCounts.neutraal})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                <span className="text-[10px] text-neutral-500">Ongedekt ({postureCounts.dragend})</span>
              </div>
            </div>
          </div>
          <div>
            {riskFinancials.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-neutral-200">
                <table className="spk-table" data-testid="risk-posture-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Risico</th>
                      <th className="text-center">Houding</th>
                      <th className="text-center">Oplossingen</th>
                      <th className="text-right">Schade bij inactie</th>
                      <th className="text-right">Kosten oplossingen</th>
                      <th className="text-right">Verschil</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskFinancials.map(rf => {
                      const diff = (rf.cost_of_inaction || 0) - (rf.cost_of_measures || 0);
                      const postureColors = {
                        vermijdend: 'bg-green-100 text-green-700 border-green-200',
                        neutraal: 'bg-amber-100 text-amber-700 border-amber-200',
                        dragend: 'bg-red-100 text-red-700 border-red-200',
                      };
                      const postureLabels = {
                        vermijdend: 'Vermijdend',
                        neutraal: 'Neutraal',
                        dragend: 'Dragend',
                      };
                      return (
                        <tr key={rf.risk_id} className="cursor-pointer" data-testid={`posture-row-${rf.risk_id}`} onClick={() => setSelectedRisk(rf)}>
                          <td className="text-xs font-mono text-neutral-500">{rf.risk_seq_id}</td>
                          <td>
                            <span className="text-sm font-medium text-black">{rf.title}</span>
                          </td>
                          <td className="text-center">
                            <Badge className={`text-[10px] border ${postureColors[rf.posture]}`}>{postureLabels[rf.posture]}</Badge>
                          </td>
                          <td className="text-center text-sm font-medium text-neutral-600">{rf.measure_count}</td>
                          <td className="text-right text-sm font-medium">
                            {rf.cost_of_inaction ? (
                              <span className="text-red-600">{formatCurrency(rf.cost_of_inaction)}</span>
                            ) : (
                              <span className="text-[#6B7280] italic">Nog niet berekenbaar</span>
                            )}
                          </td>
                          <td className="text-right text-sm font-medium">
                            {rf.cost_of_measures ? (
                              <span className="text-blue-600">{formatCurrency(rf.cost_of_measures)}</span>
                            ) : (
                              <span className="text-[#6B7280] italic">Nog niet berekenbaar</span>
                            )}
                          </td>
                          <td className="text-right">
                            {(rf.cost_of_inaction && rf.cost_of_measures) ? (
                              <span className={`text-sm font-bold ${diff > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                              </span>
                            ) : (
                              <span className="text-[#6B7280] italic text-sm">—</span>
                            )}
                          </td>
                          <td className="text-center">
                            <ChevronRight className="h-4 w-4 text-neutral-300 mx-auto" strokeWidth={1.5} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Shield className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-neutral-400">Geen risico's gevonden. Voeg risico's toe in het Risicoregister.</p>
              </div>
            )}
          </div>
        </section>
        )}

        {/* Financiële referentiegegevens */}
        {financialData && (financialData.annual_revenue > 0 || financialData.operational_costs > 0 || financialData.personnel_costs > 0) && (
          <section className="rounded-2xl border border-neutral-200 p-7" data-testid="financial-reference-card">
            <p className="spk-section-label">Referentiegegevens</p>
            <p className="text-xs text-neutral-400 mt-0.5 mb-4">Verzameld uit je nulmeting</p>
              <div className="grid grid-cols-3 gap-6">
                <div className="p-4 bg-neutral-50 rounded-xl">
                  <p className="text-xs text-neutral-500 mb-2">Jaaromzet</p>
                  <p className={`text-lg font-bold ${financialData.annual_revenue ? 'text-black' : 'text-[#6B7280] italic'}`}>
                    {financialData.annual_revenue ? formatCurrency(financialData.annual_revenue) : 'Nog niet ingevuld'}
                  </p>
                </div>
                <div className="p-4 bg-neutral-50 rounded-xl">
                  <p className="text-xs text-neutral-500 mb-2">Operationele kosten</p>
                  <p className={`text-lg font-bold ${financialData.operational_costs ? 'text-black' : 'text-[#6B7280] italic'}`}>
                    {financialData.operational_costs ? formatCurrency(financialData.operational_costs) : 'Nog niet ingevuld'}
                  </p>
                </div>
                <div className="p-4 bg-neutral-50 rounded-xl">
                  <p className="text-xs text-neutral-500 mb-2">Personeelskosten</p>
                  <p className={`text-lg font-bold ${financialData.personnel_costs ? 'text-black' : 'text-[#6B7280] italic'}`}>
                    {financialData.personnel_costs ? formatCurrency(financialData.personnel_costs) : 'Nog niet ingevuld'}
                  </p>
                </div>
              </div>
          </section>
        )}

        {/* ROI Samenvatting — alleen tonen als er berekende data is */}
        {unmitigated > 0 && (
        <section className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-6" data-testid="roi-highlight-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="spk-section-label">Wat levert het op?</p>
              <h2 className="text-2xl font-semibold mt-1">Investeer {formatCurrency(unmitigated * 0.15)} en bespaar jaarlijks:</h2>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-black">{formatCurrency(unmitigated * 0.85)}</p>
              <p className="text-sm text-neutral-500">per jaar aan potentiele schade</p>
            </div>
          </div>
        </section>
        )}

            {/* Charts Row — alleen tonen als er berekende bedragen zijn */}
        {(unmitigated > 0 || totalExposure > 0) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk vs Mitigation Bar - Monochrome */}
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="risk-vs-mitigation-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  Kosten van risico vs oplossingen
                </CardTitle>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      <p className="font-medium mb-1">Kosten vergelijking</p>
                      <p>Gestreept = jaarlijks geschat verlies als je niets doet. Solid = eenmalige investering in oplossingen (~15% van risico). Het verschil is je potentiele besparing.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {/* Total Risk Bar - Red with pattern */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="flex items-center gap-2 text-neutral-600">
                      <span className="inline-block w-4 h-4 bg-red-600 rounded" style={{
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)'
                      }}></span>
                      Kosten van niets doen
                    </span>
                    <span className="font-semibold text-red-700">{formatCurrency(unmitigated)}</span>
                  </div>
                  <div className="h-10 bg-red-100 rounded-lg relative overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-red-600 rounded-lg flex items-center justify-end pr-3"
                      style={{ 
                        width: '100%',
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.2) 4px, rgba(255,255,255,0.2) 8px)'
                      }}
                    >
                      <span className="text-sm font-medium text-white">Jaarlijks verlies</span>
                    </div>
                  </div>
                </div>

                {/* Mitigation Cost Bar - Green solid */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="flex items-center gap-2 text-neutral-600">
                      <span className="inline-block w-4 h-4 bg-emerald-500 rounded"></span>
                      Geschatte kosten oplossingen
                    </span>
                    <span className="font-semibold text-emerald-700">{formatCurrency(unmitigated * 0.15)}</span>
                  </div>
                  <div className="h-10 bg-emerald-100 rounded-lg relative overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-emerald-500 rounded-lg flex items-center justify-end pr-3"
                      style={{ width: `${Math.min((unmitigated * 0.15 / unmitigated) * 100, 100)}%` }}
                    >
                      <span className="text-sm font-medium text-white">Eenmalig</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 pt-2 border-t border-neutral-100">
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <span className="inline-block w-3 h-3 rounded" style={{
                    background: '#dc2626',
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(255,255,255,0.3) 1px, rgba(255,255,255,0.3) 2px)'
                  }}></span>
                  Gestreept = Risico kosten
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <span className="inline-block w-3 h-3 bg-emerald-500 rounded"></span>
                  Solid = Investering
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Breakdown - Monochrome */}
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="category-breakdown-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  Risico per gebied
                </CardTitle>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      <p className="font-medium mb-1">Verdeling per gebied</p>
                      <p>Hoe is het risico verdeeld over mensen, processen en technologie? Hoe hoger, hoe meer aandacht nodig.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {['people', 'process', 'technology'].map((category, index) => {
                const amount = exposureData?.exposure_by_category?.[category] || 0;
                const percentage = totalExposure > 0 ? Math.round((amount / totalExposure) * 100) : 0;
                const colorSchemes = [
                  { bg: 'bg-blue-100', fill: 'bg-blue-600', text: 'text-blue-700' },
                  { bg: 'bg-violet-100', fill: 'bg-violet-600', text: 'text-violet-700' },
                  { bg: 'bg-cyan-100', fill: 'bg-cyan-600', text: 'text-cyan-700' },
                ];
                const scheme = colorSchemes[index];
                
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="flex items-center gap-2 text-neutral-600">
                        {getCategoryIcon(category)}
                        {getCategoryName(category)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${scheme.text}`}>{formatCurrency(amount)}</span>
                        <span className="text-xs text-neutral-400">{percentage}%</span>
                      </div>
                    </div>
                    <div className={`h-3 ${scheme.bg} rounded-full relative overflow-hidden`}>
                      <div 
                        className={`absolute inset-y-0 left-0 ${scheme.fill} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Risk Level Breakdown - Colorful */}
              <div className="pt-4 mt-4 border-t border-neutral-100">
                <p className="text-xs font-medium text-neutral-500 mb-3">Per risiconiveau</p>
                <div className="grid grid-cols-3 gap-6">
                  {[
                    { level: 'high', label: 'Hoog', shade: 'bg-red-600 text-white' },
                    { level: 'medium', label: 'Midden', shade: 'bg-amber-500 text-white' },
                    { level: 'low', label: 'Laag', shade: 'bg-emerald-500 text-white' },
                  ].map((item) => {
                    const amount = exposureData?.exposure_by_level?.[item.level] || 0;
                    return (
                      <div key={item.level} className={`p-3 rounded-lg ${item.shade}`}>
                        <p className="text-lg font-bold">
                          {amount > 0 ? formatCurrencyShort(amount) : '-'}
                        </p>
                        <p className="text-xs opacity-80">{item.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        ) : (
          <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7 text-center" data-testid="financial-no-data-charts">
            <p className="text-sm font-medium text-[#111827]">Nog niet berekenbaar</p>
            <p className="text-xs text-[#6B7280] mt-1">Vul de geschatte schade per risico in om grafieken en berekeningen te zien.</p>
          </section>
        )}

        {/* Details */}
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="top-risk-actions-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  Grootste financiele impact
                </CardTitle>
                <Link to="/risks" className="text-sm text-neutral-500 hover:text-black transition-colors">
                  Alle risico's
                </Link>
              </CardHeader>
          <CardContent>
            {topRiskActions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-100">
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-400 py-3 pr-4">Risico</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-400 py-3 px-4">Categorie</th>
                      <th className="text-right text-xs font-bold uppercase tracking-wider text-neutral-400 py-3 px-4">Schade bij niets doen</th>
                      <th className="text-right text-xs font-bold uppercase tracking-wider text-neutral-400 py-3 pl-4">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRiskActions.map((action, idx) => (
                      <tr key={action.id} className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors">
                        <td className="py-4 pr-4">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-black">{action.title}</p>
                            {idx === 0 && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white shrink-0" data-testid="financial-begin-hier">Begin hier</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="flex items-center gap-2 text-sm text-neutral-600">
                            {getCategoryIcon(action.risk_type)}
                            {getCategoryName(action.risk_type)}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-sm font-semibold text-red-600">
                            {formatCurrency(action.cost_of_inaction)}
                          </span>
                        </td>
                        <td className="py-4 pl-4 text-right">
                          <Button 
                            size="sm" 
                            variant={idx === 0 ? 'default' : 'outline'}
                            className={idx === 0 ? 'bg-black text-white hover:bg-neutral-800' : 'border-neutral-200'}
                            onClick={() => { setDecisionAction(action); setDecisionStep(1); }}
                            data-testid={`action-decide-${action.id}`}
                          >
                            Neem beslissing
                            <ArrowRight className="ml-1 h-3 w-3" strokeWidth={1.5} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-black">Alle risico's geadresseerd!</p>
                <p className="text-xs text-neutral-500 mt-1">Er zijn geen openstaande risico's met financiële impact.</p>
              </div>
            )}
          </CardContent>
        </Card>

            {/* NEXT STEP */}
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="financial-next-step">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="spk-section-label mb-0">Volgende stap</p>
                    {topRiskActions.length > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mt-1">
                    {topRiskActions.length > 0 ? 'Neem beslissingen over je grootste risico\'s' : 'Bekijk je beveiligingsstrategie'}
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    {topRiskActions.length > 0 ? 'Kies per risico of je het oplost, accepteert of uitstelt.' : 'Je financiële risico\'s zijn onder controle. Bekijk de strategie voor lange-termijn verbeteringen.'}
                  </p>
                </div>
                <Button
                  onClick={() => navigate(topRiskActions.length > 0 ? '/risks' : '/strategy')}
                  className="bg-black text-white hover:bg-neutral-800 shrink-0"
                  data-testid="financial-goto-next"
                >
                  {topRiskActions.length > 0 ? 'Bekijk risico\'s' : 'Bekijk strategie'}
                  <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                </Button>
              </div>
            </section>
          </main>
        </div>
      <Dialog open={!!decisionAction} onOpenChange={() => setDecisionAction(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="decision-wizard">
          <DialogHeader>
            <DialogTitle>Beslissing: {decisionAction?.title}</DialogTitle>
            <DialogDescription>
              {decisionStep === 1 && 'Stap 1 van 3: Over dit risico'}
              {decisionStep === 2 && 'Stap 2 van 3: Wat kost het?'}
              {decisionStep === 3 && 'Stap 3 van 3: Wat wil je doen?'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-1 px-1" data-testid="decision-steps">
            {[1,2,3].map(s => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= decisionStep ? 'bg-black' : 'bg-neutral-200'}`} />
            ))}
          </div>

          {decisionAction && (
            <>
              {/* Step 1: Risk Information */}
              {decisionStep === 1 && (
                <div className="space-y-4">
                  <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                    <h4 className="text-sm font-semibold text-black mb-2">{decisionAction.title}</h4>
                    <p className="text-sm text-neutral-600">{decisionAction.description || 'Dit risico vraagt aandacht op basis van je huidige beveiligingsprofiel.'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">Categorie</p>
                      <p className="text-sm font-medium text-black flex items-center gap-1.5 mt-1">
                        {getCategoryIcon(decisionAction.risk_type)}
                        {getCategoryName(decisionAction.risk_type)}
                      </p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                      <p className="text-xs text-red-600">Geschat risico</p>
                      <p className="text-lg font-bold text-red-700 mt-1">{formatCurrency(decisionAction.cost_of_inaction)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Cost vs Benefit */}
              {decisionStep === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-red-600" strokeWidth={1.5} />
                        <span className="text-xs font-semibold text-red-800">Kosten van niets doen</span>
                      </div>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(decisionAction.cost_of_inaction)}</p>
                      <p className="text-[10px] text-red-600 mt-1">Geschat jaarlijks verlies</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-green-600" strokeWidth={1.5} />
                        <span className="text-xs font-semibold text-green-800">Investering in oplossing</span>
                      </div>
                      <p className="text-xl font-bold text-green-700">{formatCurrency(Math.round(decisionAction.cost_of_inaction * 0.15))}</p>
                      <p className="text-[10px] text-green-600 mt-1">Eenmalige investering (~15%)</p>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <PiggyBank className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
                      <span className="text-xs font-semibold text-blue-800">Potentiële besparing</span>
                    </div>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(Math.round(decisionAction.cost_of_inaction * 0.85))}</p>
                    <p className="text-[10px] text-blue-600 mt-1">Per jaar door risicoreductie</p>
                  </div>
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-600">ROI</span>
                      <span className="text-sm font-bold text-green-700">{Math.round(((decisionAction.cost_of_inaction * 0.85) / (decisionAction.cost_of_inaction * 0.15)) * 100)}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Decision */}
              {decisionStep === 3 && (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-600 mb-2">Kies een actie voor dit risico:</p>
                  <button
                    className="w-full p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left flex items-center gap-6"
                    onClick={() => { toast.success(`Actie "${decisionAction.title}" gestart, oplossing wordt gepland`); setDecisionAction(null); }}
                    data-testid="decision-execute"
                  >
                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-black">Uitvoeren</p>
                      <p className="text-xs text-neutral-500">Oplossing plannen en starten met implementatie</p>
                    </div>
                  </button>
                  <button
                    className="w-full p-4 border-2 border-amber-200 rounded-lg hover:bg-amber-50 transition-colors text-left flex items-center gap-6"
                    onClick={() => { toast.info(`Actie "${decisionAction.title}" uitgesteld`); setDecisionAction(null); }}
                    data-testid="decision-defer"
                  >
                    <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-black">Uitstellen</p>
                      <p className="text-xs text-neutral-500">Later opnieuw beoordelen. Risico blijft openstaan</p>
                    </div>
                  </button>
                  <button
                    className="w-full p-4 border-2 border-red-200 rounded-lg hover:bg-red-50 transition-colors text-left flex items-center gap-6"
                    onClick={() => { toast.warning(`Actie "${decisionAction.title}" geaccepteerd als risico`); setDecisionAction(null); }}
                    data-testid="decision-accept"
                  >
                    <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <Ban className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-black">Risico accepteren</p>
                      <p className="text-xs text-neutral-500">Bewust besluit om geen oplossing te implementeren</p>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}

          <DialogFooter className="gap-2">
            {decisionStep > 1 && (
              <Button variant="outline" onClick={() => setDecisionStep(decisionStep - 1)} data-testid="decision-back">
                <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />
                Terug
              </Button>
            )}
            <div className="flex-1" />
            {decisionStep < 3 && (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => setDecisionStep(decisionStep + 1)} data-testid="decision-next">
                Volgende
                <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Risk Detail Right Panel */}
      <RightPanel
        open={!!selectedRisk}
        onClose={() => setSelectedRisk(null)}
        title={selectedRisk?.title || 'Risico'}
        subtitle={selectedRisk?.risk_seq_id || ''}
        testId="financial-risk-panel"
        chain={
          <ChainVisual
            activeStep="risk"
            data={{
              asset: selectedRisk?.asset_name || '',
              vulnerability: '',
              threat: '',
              risk: selectedRisk?.title || '',
              measure: selectedRisk?.measure_count ? `${selectedRisk.measure_count} maatr.` : '',
            }}
            testId="financial-chain-visual"
          />
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setSelectedRisk(null)}>Sluiten</Button>
            <div className="flex-1" />
            <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { setSelectedRisk(null); navigate('/measures'); }}>
              Voeg oplossing toe <ArrowRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
            </Button>
          </>
        }
      >
        {selectedRisk && (
          <div className="space-y-5">
            {/* Posture badge */}
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] border ${selectedRisk.posture === 'vermijdend' ? 'bg-green-100 text-green-700 border-green-200' : selectedRisk.posture === 'neutraal' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                {selectedRisk.posture === 'vermijdend' ? 'Vermijdend' : selectedRisk.posture === 'neutraal' ? 'Neutraal' : 'Dragend'}
              </Badge>
              <span className="text-xs text-neutral-500">{selectedRisk.measure_count} oplossing(en)</span>
            </div>

            {/* DecisionRow: Schade-inschatting */}
            <DecisionRow
              label="Jaarlijkse schade-inschatting"
              value={formatCurrency(selectedRisk.cost_of_inaction || 0)}
              type="recommended"
              rationale="Gebaseerd op risicoscore, impactklasse en branche-gemiddelden"
              confirmed={false}
              onConfirm={() => setSelectedRisk(null)}
              onEdit={() => { setSelectedRisk(null); navigate('/risks'); }}
              confirmLabel="Accepteer berekening"
              editLabel="Pas aannames aan"
            />

            {/* DecisionRow: ROI */}
            {(() => {
              const diff = (selectedRisk.cost_of_inaction || 0) - (selectedRisk.cost_of_measures || 0);
              return diff > 0 ? (
                <DecisionRow
                  label="Return on Investment"
                  value={`${formatCurrency(diff)} besparing per jaar`}
                  type="auto"
                  rationale={`Investering van ${formatCurrency(selectedRisk.cost_of_measures || 0)} bespaart ${formatCurrency(diff)} aan potentiele schade`}
                  confirmed={true}
                />
              ) : (
                <DecisionRow
                  label="Kosten-batenanalyse"
                  value={`Kosten overschrijden verwachte schade met ${formatCurrency(Math.abs(diff))}`}
                  type="required"
                  rationale="Overweeg of de maatregel noodzakelijk is vanuit compliance of strategisch belang"
                  onConfirm={() => { setSelectedRisk(null); navigate('/risks'); }}
                  confirmLabel="Bekijk alternatieven"
                />
              );
            })()}

            {/* Financial impact cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-1">Schade bij inactie</p>
                <p className={`text-lg font-bold ${selectedRisk.cost_of_inaction ? 'text-red-700' : 'text-[#6B7280] italic'}`}>
                  {selectedRisk.cost_of_inaction ? formatCurrency(selectedRisk.cost_of_inaction) : 'Nog niet berekenbaar'}
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500 mb-1">Kosten oplossingen</p>
                <p className={`text-lg font-bold ${selectedRisk.cost_of_measures ? 'text-blue-700' : 'text-[#6B7280] italic'}`}>
                  {selectedRisk.cost_of_measures ? formatCurrency(selectedRisk.cost_of_measures) : 'Nog niet berekenbaar'}
                </p>
              </div>
            </div>

            {/* Calculation source */}
            <div className="pt-4 border-t border-neutral-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Berekeningsbron</p>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-500">Categorie</span>
                  <span className="font-medium text-black">{getCategoryName(selectedRisk.category)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-500">Risicoscore</span>
                  <span className="font-medium text-black">{selectedRisk.risk_score || '-'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-500">Methode</span>
                  <span className="font-medium text-black">Risico x Impact</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </RightPanel>
    </PageShell>
  );
};

export default FinancialPage;
